import React, { useRef, useState } from 'react';

import { AxiosError } from 'axios';
import { CircularProgress } from '@mui/material';
import { reverse } from 'named-urls';
import { useNavigate } from 'react-router-dom';

import API from 'utils/api/api';
import { getErrorMessage } from 'utils/api/errors';
import { ScheduleStatuses } from 'utils/api/modules/schedules/schedules.types';
import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import DataError from 'containers/Error/DataError';

import { bindAuditDraft } from 'components/Audits/auditDraft';

import { DrawerWrapper, LoadingContainer } from './styles';
import { ScheduleDrawerProps } from './types';
import { useScheduleInstance } from './useScheduleInstance';
import DrawerHeader from './DrawerHeader';
import DrawerContent from './DrawerContent';
import DrawerFooter from './DrawerFooter';
import WontDoModal from './WontDoModal';

const ScheduleDrawer: React.FC<ScheduleDrawerProps> = ({ itemId, scheduleId, onClose, onUpdate }) => {
  const { notification } = useStore();
  const navigate = useNavigate();

  const { loading, data, error, links, filteredEntityModules, assigneeLoading, handleAssigneeChange } =
    useScheduleInstance(scheduleId, itemId, onUpdate, onClose);

  const [wontDoModalOpen, setWontDoModalOpen] = useState(false);
  const [wontDoReason, setWontDoReason] = useState('');
  const [wontDoLoading, setWontDoLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  // `disabled` only closes the window once React has committed the re-render, which a same-tick
  // double-click beats — and the start endpoint takes no lock, so two of them race.
  const inFlight = useRef(false);

  const itemName = data?.schedule?.name || 'Schedule Details';

  const recordPath = (auditUuid: string): string =>
    reverse(routes.dashboard.audits.forms.details, { formId: auditUuid });

  const openFillOut = async (auditUuid: string): Promise<boolean> => {
    const draftId = await bindAuditDraft(API, auditUuid, notification);
    navigate(draftId ? reverse(routes.dashboard.audits.draft.details, { draftId }) : recordPath(auditUuid));
    return draftId !== undefined;
  };

  const handleStartAudit = async (): Promise<void> => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setAuditLoading(true);
    try {
      const response = await API.schedules.startAuditFromSchedule(scheduleId, itemId)();
      onUpdate?.();
      const auditUuid = response.data.inspection?.uuid;
      if (auditUuid) {
        if (!response.data.submissionForm) {
          navigate(recordPath(auditUuid));
        } else if (!(await openFillOut(auditUuid))) {
          return;
        }
      }
      notification.enqueueSuccessSnackbar('Audit started. Redirecting to the audit');
    } catch (e) {
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Failed to start the audit'));
    } finally {
      inFlight.current = false;
      setAuditLoading(false);
    }
  };

  const handleContinueAudit = async (): Promise<void> => {
    const auditUuid = data?.inspection?.uuid;
    if (!auditUuid) {
      return;
    }

    if (!data?.submissionForm) {
      navigate(recordPath(auditUuid));
      return;
    }

    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setAuditLoading(true);
    try {
      await openFillOut(auditUuid);
    } finally {
      inFlight.current = false;
      setAuditLoading(false);
    }
  };

  const handleViewAudit = (): void => {
    const auditUuid = data?.inspection?.uuid;
    if (auditUuid) {
      navigate(recordPath(auditUuid));
    }
  };

  const handleWontDoClose = (): void => {
    if (!wontDoLoading) {
      setWontDoModalOpen(false);
      setWontDoReason('');
    }
  };

  const handleWontDoConfirm = async (): Promise<void> => {
    setWontDoLoading(true);
    try {
      await API.schedules.updateScheduleInstance(scheduleId, itemId, {
        status: ScheduleStatuses.WONT_DO,
        reason: wontDoReason,
      })();
      notification.enqueueSuccessSnackbar('Status updated successfully');
      setWontDoModalOpen(false);
      setWontDoReason('');
      onUpdate?.();
      onClose();
    } catch (e) {
      notification.enqueueErrorSnackbar(getErrorMessage(e as AxiosError, 'Failed to update status'));
    } finally {
      setWontDoLoading(false);
    }
  };

  return (
    <DrawerWrapper>
      <DrawerHeader title={itemName} onClose={onClose} />

      {loading && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {!loading && error && <DataError />}

      {!loading && !error && data && (
        <>
          <DrawerContent
            data={data}
            links={links}
            filteredEntityModules={filteredEntityModules}
            assigneeLoading={assigneeLoading}
            onAssigneeChange={handleAssigneeChange}
          />
          <DrawerFooter
            data={data}
            auditLoading={auditLoading}
            onStartAudit={handleStartAudit}
            onContinueAudit={handleContinueAudit}
            onViewAudit={handleViewAudit}
            onWontDoClick={() => setWontDoModalOpen(true)}
          />
        </>
      )}

      <WontDoModal
        open={wontDoModalOpen}
        itemName={itemName}
        reason={wontDoReason}
        loading={wontDoLoading}
        onReasonChange={setWontDoReason}
        onClose={handleWontDoClose}
        onConfirm={handleWontDoConfirm}
      />
    </DrawerWrapper>
  );
};

export default ScheduleDrawer;
